/**
 * @format
 */
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import lodash from 'lodash';

const debug = require('debug')('advertsSlice');

import {
  getAdverts,
  Advertisement,
  CategoryList,
  FullCategory,
} from '../api/adserver';

import {
  AdvertCategoriesState,
  ExploreCategoryType,
} from '../store/advertCategoriesSlice';

import { RootState } from './rootReducer';
import { AppThunk } from './store';

interface AdvertListPayload {
  adverts: FullCategory | null;
}

type AdvertListsPayload = CategoryList;

type AdvertListState = {
  page: number;
  error: string | null;
  isLoading: boolean;
  categoryAdvertList: CategoryList;
  current: FullCategory | null;
};

let initialState: AdvertListState = {
  page: 0,
  isLoading: false,
  error: null,
  categoryAdvertList: {},
  current: null,
};

const getCategoriesDone = (state: AdvertListState) => {
  state.isLoading = false;
};

const advertsSlice = createSlice({
  name: 'adverts',
  initialState,
  reducers: {
    displayCategoryAdvertList(state, action: PayloadAction<AdvertListPayload>) {
      const { adverts } = action.payload;
      state.current = adverts;
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    getAdvertsListSuccess(state, action: PayloadAction<AdvertListsPayload>) {
      getCategoriesDone(state);
      state.categoryAdvertList = action.payload;
      state.error = null;
    },
    getAdvertsListFailed(state, action: PayloadAction<string>) {
      getCategoriesDone(state);
      state.categoryAdvertList = {};
      state.error = action.payload;
    },
    getAdvertsListStart(state) {
      state.isLoading = true;
    },
  },
});

export const {
  displayCategoryAdvertList,
  setCurrentPage,
  getAdvertsListSuccess,
  getAdvertsListFailed,
  getAdvertsListStart,
} = advertsSlice.actions;

export const selectCategoryAdvertsList = createSelector(
  [
    (state: RootState) => state.adverts,
    (state: RootState) => state.advertCategories,
  ],
  (advertSlice: AdvertListState, categorySlice: AdvertCategoriesState) => {
    // debug('selectCategoryAdvertsList categoryAdvertList are:');
    // debug(advertSlice);
    const { isLoading, categoryAdvertList } = advertSlice;
    const { categories } = categorySlice;
    const filteredCategoryAdvertList = lodash.pickBy(
      categoryAdvertList,
      (cat) => !cat.featured && cat.title !== 'Promoted Feature',
    );
    const filteredList = lodash.flatMap(
      Object.values(filteredCategoryAdvertList).map((c) => c),
    );
    const advertsList = filteredList.reduce<Advertisement[]>((acc, cat) => {
      const categoryAdverts = lodash.flatMap(cat.adverts, (advert) => advert);
      return lodash.unionBy(acc, categoryAdverts, 'id');
    }, []);

    const categoryIds = categories.map((category) => {
      if (category.category_type === ExploreCategoryType.ExploreInternal) {
        return category.id;
      }
    });

    const filteredCategoryIds = categoryIds.filter((Id) => {
      if (Id) {
        return true;
      }
      return false;
    });

    filteredList.sort((a, b) => {
      if (a.id && b.id) {
        return (
          filteredCategoryIds.indexOf(a.id) - filteredCategoryIds.indexOf(b.id)
        );
      }
      return 0;
    });
    return {
      advertsList,
      categoryAdvertList: filteredList,
      isLoading,
    };
  },
);

export const selectAdvertsList = createSelector(
  (state: RootState) => state.adverts,
  (advertSlice: AdvertListState) => {
    //debug('categoryAdvertList are:');
    //debug(advertSlice.categoryAdvertList);
    const { isLoading, categoryAdvertList } = advertSlice;
    const filteredCategoryAdvertList = lodash.pickBy(
      categoryAdvertList,
      (cat) => !cat.featured && cat.title !== 'Promoted Feature',
    );
    const filteredList = lodash.flatMap(
      Object.values(filteredCategoryAdvertList).map((c) => c),
    );
    const advertsList = filteredList.reduce<Advertisement[]>((acc, cat) => {
      const categoryAdverts = lodash.flatMap(cat.adverts, (advert) => advert);
      return lodash.unionBy(acc, categoryAdverts, 'id');
    }, []);
    return {
      advertsList,
      isLoading,
    };
  },
);

export const selectFeaturedList = createSelector(
  (state: RootState) => state.adverts,
  (advertSlice: AdvertListState) => {
    // debug('selectFeaturedList - categoryAdvertList are:');
    // debug(advertSlice.categoryAdvertList);
    const { isLoading, categoryAdvertList } = advertSlice;
    const filteredCategoryAdvertList = lodash.pickBy(
      categoryAdvertList,
      (cat) => cat.featured,
    );
    const featuredCategoryKey = (
      Object.keys(categoryAdvertList) as Array<string>
    ).find((key) => categoryAdvertList[key].featured);
    const featuredCategory = featuredCategoryKey
      ? filteredCategoryAdvertList[featuredCategoryKey]
      : null;
    const featuredList = lodash.flatMap(
      Object.values(filteredCategoryAdvertList).map((c) => c.adverts),
    );
    return {
      featuredList,
      isLoading,
      featuredCategory,
    };
  },
);

export const fetchAdvertsList =
  (building: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getAdvertsListStart());
      const categories = await getAdverts(building);
      dispatch(getAdvertsListSuccess(categories));
    } catch (err: unknown) {
      let message = 'unknown error';
      if (err instanceof Error) {
        message = err.toString();
      }
      dispatch(getAdvertsListFailed(message));
    }
  };

export const selectPromotedFeatureAd = createSelector(
  (state: RootState) => state.adverts,
  (advertSlice: AdvertListState) => {
    const promotedCategory = Object.values(advertSlice.categoryAdvertList)
      .find((cat) => cat.title === 'Promoted Feature');
    return promotedCategory?.adverts?.[0] ?? null;
  },
);

export default advertsSlice.reducer;
